import {
  EU_OPEN_BANKING_PAYMENT_CURRENCY_CODES,
  PayableEntityType,
} from "@shared_packages/toolkit/types";
import {
  PaymentsPricingMode,
  SubscriptionFeePricingStrategy,
} from "../../../../shared-packages/packages/toolkit/types/src/company-subscription-fees-types";
import { SubscriptionPriceProfileKey } from "../../../../shared-packages/packages/toolkit/types/src/subscription-price-profile-types";
import type {
  SubscriptionFeeRule,
  SubscriptionFeeSchedule,
} from "../../../../util/types";

export const PAYABLE_ENTITY_TYPES = [
  PayableEntityType.PAYMENT,
  PayableEntityType.RECURRING_PAYMENT,
  PayableEntityType.BULK_PAYMENT,
  PayableEntityType.VRP,
] as const;

export type PayableEntityTypeOption = PayableEntityType;

export const PO_BILLING_FEE_CURRENCY_CODES = [
  "GBP",
  ...EU_OPEN_BANKING_PAYMENT_CURRENCY_CODES.map((currency) =>
    currency.toUpperCase()
  ),
];

export const FEE_CURRENCY_SYMBOLS: Record<string, string> = {
  gbp: "£",
  eur: "€",
  sek: "kr",
  czk: "Kč",
  ron: "lei",
  pln: "zł",
  bgn: "лв",
  huf: "Ft",
  dkk: "kr",
};

export const FEE_TYPE_ROWS: { type: PayableEntityType; label: string }[] = [
  { type: PayableEntityType.PAYMENT, label: "Single payment" },
  { type: PayableEntityType.RECURRING_PAYMENT, label: "Recurring" },
  { type: PayableEntityType.BULK_PAYMENT, label: "Bulk" },
  { type: PayableEntityType.VRP, label: "VRP" },
];

export const BULK_FIXED_MONTHLY_PLAN_OPTIONS = [
  {
    key: SubscriptionPriceProfileKey.BULK_PAYMENTS_FIXED_MONTHLY_1000_GBP,
    label: "£29.99 / month, up to 1,000 tx",
    currency: "gbp",
    monthlyFeeMinor: 2999,
    transactionLimit: 1000,
  },
  {
    key: SubscriptionPriceProfileKey.BULK_PAYMENTS_FIXED_MONTHLY_5000_GBP,
    label: "£49.99 / month, up to 5,000 tx",
    currency: "gbp",
    monthlyFeeMinor: 4999,
    transactionLimit: 5000,
  },
  {
    key: SubscriptionPriceProfileKey.BULK_PAYMENTS_FIXED_MONTHLY_1000_EUR,
    label: "EUR 29.99 / month, up to 1,000 tx",
    currency: "eur",
    monthlyFeeMinor: 2999,
    transactionLimit: 1000,
  },
  {
    key: SubscriptionPriceProfileKey.BULK_PAYMENTS_FIXED_MONTHLY_5000_EUR,
    label: "EUR 49.99 / month, up to 5,000 tx",
    currency: "eur",
    monthlyFeeMinor: 4999,
    transactionLimit: 5000,
  },
  {
    key: SubscriptionPriceProfileKey.BULK_PAYMENTS_FIXED_MONTHLY_1000_USD,
    label: "USD 29.99 / month, up to 1,000 tx",
    currency: "usd",
    monthlyFeeMinor: 2999,
    transactionLimit: 1000,
  },
  {
    key: SubscriptionPriceProfileKey.BULK_PAYMENTS_FIXED_MONTHLY_5000_USD,
    label: "USD 49.99 / month, up to 5,000 tx",
    currency: "usd",
    monthlyFeeMinor: 4999,
    transactionLimit: 5000,
  },
] as const;

export function bulkFixedMonthlyPlanLabel(
  key: SubscriptionPriceProfileKey | string | null | undefined
): string {
  return (
    BULK_FIXED_MONTHLY_PLAN_OPTIONS.find((option) => option.key === key)
      ?.label ?? String(key || "Catalogue plan")
  );
}

export function defaultBulkmonthlyPlanKey(
  billingCurrency: string | null | undefined
): SubscriptionPriceProfileKey {
  const currency = String(billingCurrency || "gbp").toLowerCase();
  return (
    BULK_FIXED_MONTHLY_PLAN_OPTIONS.find(
      (option) => option.currency === currency
    )?.key ?? SubscriptionPriceProfileKey.BULK_PAYMENTS_FIXED_MONTHLY_1000_GBP
  );
}

export function preventNumberInputWheel(event: {
  currentTarget: { blur: () => void };
}) {
  event.currentTarget.blur();
}

export function emptyFeeRule(
  payableEntityType = PayableEntityType.PAYMENT,
  pricingStrategy = SubscriptionFeePricingStrategy.CUSTOM_FEES
): SubscriptionFeeRule {
  return {
    payableEntityType,
    pricingStrategy,
    flatFeeMinor: null,
    percentageFee: null,
    monthlyPlanKey: null,
    monthlyFeeMinor: null,
    monthlyUnitLimit: null,
  };
}

export function emptyCurrencySchedule(
  currency = "gbp"
): SubscriptionFeeSchedule {
  return {
    currency: currency.toLowerCase(),
    minimumMonthlyFeeMinor: null,
    rules: [
      emptyFeeRule(PayableEntityType.PAYMENT),
      emptyFeeRule(PayableEntityType.RECURRING_PAYMENT),
    ],
  };
}

export function emptySchedule(currency = "gbp"): SubscriptionFeeSchedule[] {
  return [emptyCurrencySchedule(currency)];
}

export function scheduleFromLegacyScalars(params: {
  customFlatFeePerTransaction?: number | null;
  customPercentageFee?: number | null;
  customPercentageFeeMinimum?: number | null;
}): SubscriptionFeeSchedule[] {
  return [
    {
      currency: "gbp",
      minimumMonthlyFeeMinor: params.customPercentageFeeMinimum ?? null,
      rules: [
        PayableEntityType.PAYMENT,
        PayableEntityType.RECURRING_PAYMENT,
      ].map((payableEntityType) => ({
        payableEntityType,
        pricingStrategy: SubscriptionFeePricingStrategy.CUSTOM_FEES,
        flatFeeMinor: params.customFlatFeePerTransaction ?? null,
        percentageFee: params.customPercentageFee ?? null,
        monthlyPlanKey: null,
        monthlyFeeMinor: null,
        monthlyUnitLimit: null,
      })),
    },
  ];
}

export function isCustomFeesWizardMode(mode: PaymentsPricingMode): boolean {
  return mode === PaymentsPricingMode.RULE_BASED;
}

export function pricingModeToPaymentsPricingMode(
  mode: PaymentsPricingMode
): PaymentsPricingMode | null {
  return mode === PaymentsPricingMode.BESPOKE ||
    mode === PaymentsPricingMode.MONTHLY_FEE ||
    mode === PaymentsPricingMode.RULE_BASED
    ? mode
    : null;
}

export function paymentsPricingModeToWizardMode(
  mode?: PaymentsPricingMode | null,
  scalars?: {
    customFlatFeePerTransaction?: number | null;
    customPercentageFee?: number | null;
    customPercentageFeeMinimum?: number | null;
    hasBespokePricing?: boolean;
    hasHighRiskSubscriptionPricing?: boolean;
  }
): PaymentsPricingMode {
  if (mode === PaymentsPricingMode.BESPOKE || scalars?.hasBespokePricing) {
    return PaymentsPricingMode.BESPOKE;
  }
  if (mode === PaymentsPricingMode.MONTHLY_FEE) {
    return mode;
  }
  if (mode === PaymentsPricingMode.RULE_BASED) return mode;
  if (
    scalars?.customPercentageFee != null ||
    scalars?.customFlatFeePerTransaction != null ||
    scalars?.customPercentageFeeMinimum != null
  ) {
    return PaymentsPricingMode.RULE_BASED;
  }
  return PaymentsPricingMode.RULE_BASED;
}

function normalizeRule(rule: any): SubscriptionFeeRule | null {
  if (!rule?.payableEntityType) return null;
  return {
    payableEntityType: rule.payableEntityType,
    pricingStrategy:
      rule.pricingStrategy ?? SubscriptionFeePricingStrategy.CUSTOM_FEES,
    flatFeeMinor: rule.flatFeeMinor ?? null,
    percentageFee: rule.percentageFee ?? null,
    monthlyPlanKey: rule.monthlyPlanKey ?? null,
    monthlyFeeMinor: rule.monthlyFeeMinor ?? null,
    monthlyUnitLimit: rule.monthlyUnitLimit ?? null,
  };
}

function isSubscriptionFeeRule(
  rule: SubscriptionFeeRule | null
): rule is SubscriptionFeeRule {
  return !!rule;
}

export function normalizeSchedule(
  fees: SubscriptionFeeSchedule[] | null | undefined
): SubscriptionFeeSchedule[] {
  if (!fees?.length) return emptySchedule();
  const seen = new Set<string>();
  const schedules: SubscriptionFeeSchedule[] = [];
  for (const entry of fees) {
    const currency = String(entry?.currency || "gbp").toLowerCase();
    if (seen.has(currency)) continue;
    seen.add(currency);
    const rules = Array.isArray(entry?.rules)
      ? entry.rules.map(normalizeRule).filter(isSubscriptionFeeRule)
      : emptyCurrencySchedule(currency).rules;
    schedules.push({
      currency,
      minimumMonthlyFeeMinor: entry?.minimumMonthlyFeeMinor ?? null,
      rules: rules.length ? rules : emptyCurrencySchedule(currency).rules,
    });
  }
  return schedules.length ? schedules : emptySchedule();
}

export function gbpPaymentRule(
  fees: SubscriptionFeeSchedule[] | null | undefined
): SubscriptionFeeRule | undefined {
  const gbp = normalizeSchedule(fees).find(
    (schedule) => schedule.currency === "gbp"
  );
  return gbp?.rules.find(
    (rule) => rule.payableEntityType === PayableEntityType.PAYMENT
  );
}

export function ensureGbpSchedule(
  fees: SubscriptionFeeSchedule[] | null | undefined
): SubscriptionFeeSchedule[] {
  const current = normalizeSchedule(fees);
  if (current.some((schedule) => schedule.currency === "gbp")) {
    return current;
  }
  return [emptyCurrencySchedule("gbp"), ...current];
}

export function feeRuleByType(
  schedule: SubscriptionFeeSchedule,
  payableEntityType: PayableEntityType
): SubscriptionFeeRule | undefined {
  return schedule.rules.find(
    (rule) => rule.payableEntityType === payableEntityType
  );
}

function isEmptyFeeRule(rule: SubscriptionFeeRule): boolean {
  return (
    rule.pricingStrategy === SubscriptionFeePricingStrategy.CUSTOM_FEES &&
    rule.flatFeeMinor == null &&
    rule.percentageFee == null &&
    rule.monthlyPlanKey == null &&
    rule.monthlyFeeMinor == null &&
    rule.monthlyUnitLimit == null
  );
}

export function compactScheduleForSave(
  fees: SubscriptionFeeSchedule[] | null | undefined
): SubscriptionFeeSchedule[] {
  return normalizeSchedule(fees)
    .map((schedule) => ({
      currency: schedule.currency,
      minimumMonthlyFeeMinor: schedule.minimumMonthlyFeeMinor,
      rules: schedule.rules.filter((rule) => !isEmptyFeeRule(rule)),
    }))
    .filter(
      (schedule) =>
        schedule.rules.length > 0 || schedule.minimumMonthlyFeeMinor != null
    );
}

export function scheduleHasPricedContent(
  fees: SubscriptionFeeSchedule[] | null | undefined
): boolean {
  return compactScheduleForSave(fees).some(
    (schedule) =>
      schedule.minimumMonthlyFeeMinor != null ||
      schedule.rules.some(
        (rule) =>
          rule.pricingStrategy !== SubscriptionFeePricingStrategy.CUSTOM_FEES ||
          (rule.flatFeeMinor != null && rule.flatFeeMinor > 0) ||
          (rule.percentageFee != null && rule.percentageFee > 0) ||
          rule.monthlyPlanKey != null ||
          (rule.monthlyFeeMinor != null && rule.monthlyFeeMinor > 0)
      )
  );
}

export function formatMoneyFromPence(pence: number, currency: string): string {
  const code = currency.toLowerCase();
  const amount = (pence / 100).toFixed(2);
  const symbol = FEE_CURRENCY_SYMBOLS[code];
  if (code === "gbp" || code === "eur") return `${symbol}${amount}`;
  return symbol ? `${amount} ${symbol}` : `${amount} ${code.toUpperCase()}`;
}

export function payableTypeLabel(type: string | null | undefined): string {
  const key = type ?? PayableEntityType.PAYMENT;
  return (
    FEE_TYPE_ROWS.find((row) => row.type === key)?.label ??
    type ??
    "Single payment"
  );
}

export function formatFeeScheduleSummary(
  fees: SubscriptionFeeSchedule[] | null | undefined
): string {
  const schedules = compactScheduleForSave(fees);
  if (!schedules.length) return "—";
  return schedules
    .map((schedule) => {
      const bits = schedule.rules.map((rule) => {
        const parts: string[] = [];
        if (
          rule.pricingStrategy ===
          SubscriptionFeePricingStrategy.STANDARD_TIERED
        ) {
          return `${payableTypeLabel(rule.payableEntityType)} standard tiered`;
        }
        if (
          rule.pricingStrategy ===
          SubscriptionFeePricingStrategy.HIGH_RISK_TIERED
        ) {
          return `${payableTypeLabel(rule.payableEntityType)} high-risk tiered`;
        }
        if (
          rule.pricingStrategy ===
          SubscriptionFeePricingStrategy.FIXED_MONTHLY_PLAN
        ) {
          const plan =
            rule.monthlyPlanKey != null
              ? bulkFixedMonthlyPlanLabel(rule.monthlyPlanKey)
              : `${formatMoneyFromPence(
                  rule.monthlyFeeMinor ?? 0,
                  schedule.currency
                )}/mo, up to ${(rule.monthlyUnitLimit ?? 0).toLocaleString(
                  "en-GB"
                )} tx`;
          return `${payableTypeLabel(rule.payableEntityType)} ${plan}`;
        }
        if (rule.flatFeeMinor != null) {
          parts.push(
            `${formatMoneyFromPence(rule.flatFeeMinor, schedule.currency)}/tx`
          );
        }
        if (rule.percentageFee != null) {
          parts.push(`${rule.percentageFee}%`);
        }
        return `${payableTypeLabel(rule.payableEntityType)} ${parts.join(
          " + "
        )}`;
      });
      if (schedule.minimumMonthlyFeeMinor != null) {
        bits.push(
          `min ${formatMoneyFromPence(
            schedule.minimumMonthlyFeeMinor,
            schedule.currency
          )}/mo`
        );
      }
      return `${schedule.currency.toUpperCase()}: ${bits.join("; ")}`;
    })
    .join("\n");
}

export function addCurrencySchedule(
  fees: SubscriptionFeeSchedule[],
  currency: string
): SubscriptionFeeSchedule[] {
  const current = normalizeSchedule(fees);
  const code = currency.toLowerCase();
  if (current.some((schedule) => schedule.currency === code)) {
    return current;
  }
  return [...current, emptyCurrencySchedule(code)];
}

export function removeCurrencySchedule(
  fees: SubscriptionFeeSchedule[],
  currency: string
): SubscriptionFeeSchedule[] {
  const current = normalizeSchedule(fees);
  const schedules = current.filter(
    (schedule) => schedule.currency !== currency.toLowerCase()
  );
  return schedules.length ? schedules : [emptyCurrencySchedule()];
}

export function updateCurrencySchedule(
  fees: SubscriptionFeeSchedule[],
  currency: string,
  patch: Partial<SubscriptionFeeSchedule>
): SubscriptionFeeSchedule[] {
  const current = normalizeSchedule(fees);
  return current.map((schedule) =>
    schedule.currency === currency.toLowerCase()
      ? { ...schedule, ...patch, currency: schedule.currency }
      : schedule
  );
}

export function upsertFeeRuleByType(
  fees: SubscriptionFeeSchedule[],
  currency: string,
  payableEntityType: PayableEntityType,
  patch: Partial<
    Pick<
      SubscriptionFeeRule,
      | "flatFeeMinor"
      | "percentageFee"
      | "pricingStrategy"
      | "monthlyPlanKey"
      | "monthlyFeeMinor"
      | "monthlyUnitLimit"
    >
  >
): SubscriptionFeeSchedule[] {
  const current = normalizeSchedule(fees);
  const code = currency.toLowerCase();
  const type = payableEntityType;
  return current.map((schedule) => {
    if (schedule.currency !== code) return schedule;
    const idx = schedule.rules.findIndex(
      (rule) => rule.payableEntityType === type
    );
    const existing = idx >= 0 ? schedule.rules[idx] : emptyFeeRule(type);
    const next: SubscriptionFeeRule = {
      payableEntityType: type,
      pricingStrategy:
        patch.pricingStrategy !== undefined
          ? patch.pricingStrategy
          : existing.pricingStrategy,
      flatFeeMinor:
        patch.flatFeeMinor !== undefined
          ? patch.flatFeeMinor
          : existing.flatFeeMinor,
      percentageFee:
        patch.percentageFee !== undefined
          ? patch.percentageFee
          : existing.percentageFee,
      monthlyPlanKey:
        patch.monthlyPlanKey !== undefined
          ? patch.monthlyPlanKey
          : existing.monthlyPlanKey,
      monthlyFeeMinor:
        patch.monthlyFeeMinor !== undefined
          ? patch.monthlyFeeMinor
          : existing.monthlyFeeMinor,
      monthlyUnitLimit:
        patch.monthlyUnitLimit !== undefined
          ? patch.monthlyUnitLimit
          : existing.monthlyUnitLimit,
    };
    if (idx < 0) {
      return { ...schedule, rules: [...schedule.rules, next] };
    }
    return {
      ...schedule,
      rules: schedule.rules.map((rule, i) => (i === idx ? next : rule)),
    };
  });
}
